import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { PrinterDetailForm } from "../components/printers/PrinterDetailForm";
import { Section } from "../components/Section";

export function EditPrinterPage(params: { id: number }) {
  const { data, isPending, error } = trpc.printers.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Centered>
        <Text isDanger>{error.message}</Text>
      </Centered>
    );
  }

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/printers" }}>Printers</Breadcrumb>
            <Breadcrumb>{data.name}</Breadcrumb>
          </>
        }
        actions={
          <DeletePrinterDialog printer={data}>
            <Button variant="destructive">Delete</Button>
          </DeletePrinterDialog>
        }
      >
        {data.name}
      </Section.Title>
      <PrinterDetailForm printer={data} />
    </>
  );
}

function DeletePrinterDialog({
  children,
  printer,
}: {
  children: ReactNode;
  printer: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const deletePrinter = trpc.printers.delete.useMutation();
  const navigate = useNavigate();

  const onConfirm = () => {
    deletePrinter.mutate(
      printer,
      mutationResponse({
        errorTitle: "Error deleting printer",
        successMessage: "Printer deleted",
        onSuccess() {
          setOpen(false);
          navigate({ to: "/admin/printers" });
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      danger
      open={open}
      onOpenChange={setOpen}
      title="Delete printer"
      message="Are you sure you want to delete this printer?"
      onConfirm={onConfirm}
      loading={deletePrinter.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
